import React from "react";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendBadge from "../../components/ui/TrendBadge";
import PriorityBadge from "../../components/ui/PriorityBadge";

// The trend utils are untyped JS, so statuses/dirs arrive as plain strings.
type TrendItem = {
  key: string;
  label: string;
  status: string;
  detail: string;
  /** Direction the raw number moved: "up" | "down" | "flat". */
  dir: string;
  /** Whether that move is favorable: "good" | "bad" | "neutral". */
  tone: string;
  /** False when there isn't enough data to judge (e.g. attendance). */
  hasData: boolean;
  /** Graded signed level -3..+3 (safety items only). */
  level?: number;
  levelLabel?: string;
};

type SensorTrend = {
  status: string;
  rank: number;
  level?: number;
  severity?: { band: string; label: string };
  priority?: {
    score: number;
    band: string;
    label: string;
    drivers?: string[];
  };
  safety: { status: string; items: TrendItem[] };
  behavioral: TrendItem[];
};

const HEADLINE: Record<string, string> = {
  declining: "Trending worse over the last 4 weeks.",
  improving: "Trending better over the last 4 weeks.",
  stable: "Steady over the last 4 weeks.",
};

function statusTextClass(status: string): string {
  if (status === "declining") return "text-alert-critical";
  if (status === "improving") return "text-alert-success";
  return "text-ink-3";
}

/** Color depth for a graded level: amber → red → deep-red worsening, green improving. */
function levelTextClass(level: number): string {
  const a = Math.abs(level);
  if (level > 0) {
    return a >= 3
      ? "text-alert-critical"
      : a === 2
        ? "text-alert-fire"
        : "text-alert-warning";
  }
  if (level < 0) return "text-alert-success";
  return "text-ink-3";
}

/**
 * Per-stat indicator: an arrow pointing in the direction the number actually
 * moved, colored green (good) / red (bad). Used for behavioral (neutral) rows.
 */
function DirectionArrow({ item }: { item: TrendItem }) {
  if (!item.hasData) return null;
  if (item.dir === "flat") return <span className="text-ink-3">-</span>;
  const color =
    item.tone === "good"
      ? "text-alert-success"
      : item.tone === "bad"
        ? "text-alert-critical"
        : "text-ink-3";
  const Icon = item.dir === "up" ? TrendingUpIcon : TrendingDownIcon;
  return (
    <span className={color}>
      <Icon sx={{ fontSize: 18 }} />
    </span>
  );
}

/** Graded safety row: a directional arrow (green up = improving, red down =
 *  declining) plus the magnitude label. */
function LevelTag({ item }: { item: TrendItem }) {
  if (!item.hasData) return null;
  if (!item.level) return <span className="text-ink-3 text-xs">-</span>;
  const Icon = item.level > 0 ? TrendingDownIcon : TrendingUpIcon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold ${levelTextClass(
        item.level
      )}`}
    >
      <Icon sx={{ fontSize: 16 }} />
      {item.levelLabel}
    </span>
  );
}

function TrendRow({
  item,
  graded,
}: {
  item: TrendItem;
  graded?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-black/5 last:border-0">
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink truncate">{item.label}</div>
        <div className="text-xs text-ink-3">{item.detail}</div>
      </div>
      <div className="shrink-0">
        {graded && item.level !== undefined ? (
          <LevelTag item={item} />
        ) : (
          <DirectionArrow item={item} />
        )}
      </div>
    </div>
  );
}

/**
 * Detail-page breakdown of the sensor's trend: a priority headline (how
 * problematic it is now + what's driving it), then the composite safety
 * contributors graded by intensity, and the behavioral signals.
 */
export default function SensorTrendCard({ trend }: { trend: SensorTrend }) {
  const priority = trend.priority;
  const drivers = priority?.drivers ?? [];
  return (
    <div className="rounded-card bg-white text-ink p-6 shadow-card border border-black/5">
      <div className="flex items-center justify-between gap-4 mb-1">
        <h2 className="text-xl font-semibold">Trends</h2>
        <TrendBadge level={trend.level} status={trend.status} />
      </div>
      <p className={`text-sm mb-4 ${statusTextClass(trend.status)}`}>
        {HEADLINE[trend.status]}
      </p>

      {priority && (
        <div className="flex items-center gap-3 flex-wrap mb-5 rounded-control bg-black/[0.03] px-4 py-3">
          <PriorityBadge score={priority.score} band={priority.band} />
          <span className="text-sm text-ink-2">
            {drivers.length > 0
              ? drivers.join(" · ")
              : "No active danger drivers."}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
        <div>
          <h3 className="text-xs uppercase tracking-wide text-ink-3 mb-1">
            Safety
          </h3>
          {trend.safety.items.map((item) => (
            <TrendRow key={item.key} item={item} graded />
          ))}
        </div>
        <div>
          <h3 className="text-xs uppercase tracking-wide text-ink-3 mb-1">
            Behavior
          </h3>
          {trend.behavioral.map((item) => (
            <TrendRow key={item.key} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
}
